import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Avatar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  isUserValid,
  getCurrentUser,
  logout,
  hasAdminPermission,
} from "../services/pocketbase";
import { pb } from "../services/pocketbase";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Icon } from "@iconify/react";

const Navigation = ({ onThemeToggle, currentTheme }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(getCurrentUser());
  const isLoggedIn = isUserValid();
  const isAdmin = hasAdminPermission(user);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formMenuAnchor, setFormMenuAnchor] = useState(null);
  const [leaderMenuAnchor, setLeaderMenuAnchor] = useState(null);
  const [companyMenuAnchor, setCompanyMenuAnchor] = useState(null);
  const [legalMenuAnchor, setLegalMenuAnchor] = useState(null);

  // Add state for mobile submenu expansion
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState({
    leaderboards: false,
    forms: false,
    company: false,
    legal: false,
  });

  useEffect(() => {
    const handleAuthChange = () => {
      const currentUser = getCurrentUser();
      setUser(currentUser);
    };

    // Listen for auth changes
    pb.authStore.onChange(handleAuthChange);

    // Initial check
    handleAuthChange();

    return () => {
      pb.authStore.onChange(null);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleCompanyMenuOpen = (event) => {
    setCompanyMenuAnchor(event.currentTarget);
  };

  const handleCompanyMenuClose = () => {
    setCompanyMenuAnchor(null);
  };

  const handleLegalMenuOpen = (event) => {
    setLegalMenuAnchor(event.currentTarget);
  };

  const handleLegalMenuClose = () => {
    setLegalMenuAnchor(null);
  };

  const handleFormMenuOpen = (event) => {
    setFormMenuAnchor(event.currentTarget);
  };

  const handleFormMenuClose = () => {
    setFormMenuAnchor(null);
  };

  const handleLeaderMenuOpen = (event) => {
    setLeaderMenuAnchor(event.currentTarget);
  };

  const handleLeaderMenuClose = () => {
    setLeaderMenuAnchor(null);
  };

  // Toggle mobile submenu function
  const toggleMobileSubmenu = (menu) => {
    setMobileSubmenuOpen((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "rgb(40, 44, 52)",
        boxShadow: "none",
        position: "relative",
        zIndex: 1,
        "& .MuiPaper-root": {
          background: "none",
          boxShadow: "none",
        },
        "&.MuiPaper-root": {
          background: "rgb(40, 44, 52)",
          backgroundImage: "none",
        },
        "&::before, &::after": {
          content: '""',
          position: "absolute",
          width: "115px",
          height: "20px",
          backgroundColor: "rgb(40, 44, 52)",
          backgroundImage: "none",
          bottom: "-17px",
          display: {
            xs: "none",
            md: "block",
          },
          zIndex: 0,
        },
        "&::before": {
          left: 0,
          clipPath: "polygon(0 0, 100% 0%, 82% 100%, 0% 100%)",
        },
        "&::after": {
          right: 0,
          clipPath: "polygon(0 0, 100% 0%, 100% 100%, 18% 100%)",
        },
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          minHeight: "80px !important",
        }}
      >
        {/* Left side - Logo */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <img
              src="/logo/logolarge.webp"
              height={"40px"}
              width={"40px"}
              alt="TOG Logo"
              style={{
                height: "40px",
                marginRight: "16px",
              }}
            />
          </Box>
        </Box>

        {/* Center - Navigation Buttons - Hidden on mobile */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            gap: 2,
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Button
            component={Link}
            to="/"
            sx={{
              color: "white",
              textTransform: "uppercase",
              fontSize: "0.9rem",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            Home
          </Button>
          <Button
            component={Link}
            to="/events"
            sx={{
              color: "white",
              textTransform: "uppercase",
              fontSize: "0.9rem",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            Events
          </Button>
          <Button
            component={Link}
            to="/clips"
            sx={{
              color: "white",
              textTransform: "uppercase",
              fontSize: "0.9rem",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            Clips
          </Button>

          {/* Leaderboards Dropdown */}
          <Button
            aria-controls="leaderboards-menu"
            aria-haspopup="true"
            onClick={handleLeaderMenuOpen}
            sx={{
              color: "white",
              textTransform: "uppercase",
              fontSize: "0.9rem",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            Leaderboards
            <Icon
              icon="mdi:chevron-down"
              style={{ marginLeft: "4px", fontSize: "1.2rem" }}
            />
          </Button>
          <Menu
            id="leaderboards-menu"
            anchorEl={leaderMenuAnchor}
            keepMounted
            open={Boolean(leaderMenuAnchor)}
            onClose={handleLeaderMenuClose}
            PaperProps={{
              sx: {
                backgroundColor: "rgb(40, 44, 52)",
                color: "white",
                minWidth: "200px",
                "& .MuiMenuItem-root": {
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                },
              },
            }}
          >
            <MenuItem
              component={Link}
              to="/leaderboard"
              onClick={handleLeaderMenuClose}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              Clip Leaderboard
            </MenuItem>
            <MenuItem
              component={Link}
              to="/blackops6-leaderboard"
              onClick={handleLeaderMenuClose}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              Black Ops 6 Zombies
            </MenuItem>
          </Menu>

          {/* Forms Dropdown */}
          <Button
            aria-controls="forms-menu"
            aria-haspopup="true"
            onClick={handleFormMenuOpen}
            sx={{
              color: "white",
              textTransform: "uppercase",
              fontSize: "0.9rem",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            Forms
            <Icon
              icon="mdi:chevron-down"
              style={{ marginLeft: "4px", fontSize: "1.2rem" }}
            />
          </Button>
          <Menu
            id="forms-menu"
            anchorEl={formMenuAnchor}
            keepMounted
            open={Boolean(formMenuAnchor)}
            onClose={handleFormMenuClose}
            PaperProps={{
              sx: {
                backgroundColor: "rgb(40, 44, 52)",
                color: "white",
                minWidth: "200px",
                "& .MuiMenuItem-root": {
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                },
              },
            }}
          >
            <MenuItem
              component={Link}
              to="/game-interest"
              onClick={handleFormMenuClose}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              Game Interest
            </MenuItem>
            <MenuItem
              component={Link}
              to="/staff-application"
              onClick={handleFormMenuClose}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              Staff Application
            </MenuItem>
          </Menu>

          {/* Company Dropdown */}
          <Button
            aria-controls="company-menu"
            aria-haspopup="true"
            onClick={handleCompanyMenuOpen}
            sx={{
              color: "white",
              textTransform: "uppercase",
              fontSize: "0.9rem",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            Company
            <Icon
              icon="mdi:chevron-down"
              style={{ marginLeft: "4px", fontSize: "1.2rem" }}
            />
          </Button>
          <Menu
            id="company-menu"
            anchorEl={companyMenuAnchor}
            keepMounted
            open={Boolean(companyMenuAnchor)}
            onClose={handleCompanyMenuClose}
            PaperProps={{
              sx: {
                backgroundColor: "rgb(40, 44, 52)",
                color: "white",
                minWidth: "200px",
                "& .MuiMenuItem-root": {
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                },
              },
            }}
          >
            <MenuItem
              component={Link}
              to="/faq"
              onClick={handleCompanyMenuClose}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              FAQ
            </MenuItem>
            <MenuItem
              component={Link}
              to="/discord"
              onClick={handleCompanyMenuClose}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              Discord
            </MenuItem>
            <MenuItem
              component={Link}
              to="/ranks"
              onClick={handleCompanyMenuClose}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              Ranks
            </MenuItem>
            <MenuItem
              aria-controls="legal-menu"
              aria-haspopup="true"
              onClick={(e) => {
                e.stopPropagation();
                handleLegalMenuOpen(e);
              }}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              Legal
              <Icon
                icon="mdi:chevron-right"
                style={{ marginLeft: "auto", fontSize: "1.2rem" }}
              />
            </MenuItem>
          </Menu>

          {/* Legal Submenu */}
          <Menu
            id="legal-menu"
            anchorEl={legalMenuAnchor}
            keepMounted
            open={Boolean(legalMenuAnchor)}
            onClose={handleLegalMenuClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                backgroundColor: "rgb(40, 44, 52)",
                color: "white",
                minWidth: "200px",
                "& .MuiMenuItem-root": {
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                },
              },
            }}
          >
            <MenuItem
              component={Link}
              to="/code-of-conduct"
              onClick={() => {
                handleLegalMenuClose();
                handleCompanyMenuClose();
              }}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              Code of Conduct
            </MenuItem>
            <MenuItem
              component={Link}
              to="/terms-and-policies"
              onClick={() => {
                handleLegalMenuClose();
                handleCompanyMenuClose();
              }}
              sx={{
                color: "white",
                "&:visited": { color: "white" },
                "&:hover": { color: "white" },
              }}
            >
              Terms & Policies
            </MenuItem>
          </Menu>
        </Box>

        {/* Right side - User section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Mobile menu button */}
          <IconButton
            sx={{
              display: { xs: "block", md: "none" },
              color: "white",
            }}
            onClick={() => setMobileMenuOpen(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop user section - Hide Admin and Logout on mobile */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1,
            }}
          >
            {isLoggedIn ? (
              <>
                {/* Admin button - only on desktop */}
                {isAdmin && (
                  <Button
                    component={Link}
                    to="/admin"
                    sx={{
                      color: "white",
                      textTransform: "uppercase",
                      fontSize: "0.9rem",
                      "&:visited": { color: "white" },
                      "&:hover": { color: "white" },
                    }}
                  >
                    ADMIN
                  </Button>
                )}
                {/* User Avatar */}
                <Avatar
                  src={
                    user?.avatar
                      ? `${pb.baseUrl}/api/files/users/${user.id}/${user.avatar}?thumb=40x40`
                      : null
                  }
                  sx={{ width: 32, height: 32 }}
                  imgProps={{
                    crossOrigin: "anonymous",
                    referrerPolicy: "no-referrer",
                  }}
                />
                <Button
                  component={Link}
                  to="/account"
                  sx={{
                    color: "white",
                    textTransform: "uppercase",
                    fontSize: "0.9rem",
                  }}
                >
                  {user?.username}
                </Button>

                {/* Logout button - only on desktop */}
                <Button
                  onClick={handleLogout}
                  sx={{
                    color: "white",
                    textTransform: "uppercase",
                    fontSize: "0.9rem",
                    "&:visited": { color: "white" },
                    "&:hover": { color: "white" },
                  }}
                >
                  LOGOUT
                </Button>
              </>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/login"
                  sx={{
                    color: "white",
                    textTransform: "uppercase",
                    fontSize: "0.9rem",
                    "&:visited": { color: "white" },
                    "&:hover": { color: "white" },
                  }}
                >
                  LOGIN
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  sx={{
                    color: "white",
                    textTransform: "uppercase",
                    fontSize: "0.9rem",
                    "&:visited": { color: "white" },
                    "&:hover": { color: "white" },
                  }}
                >
                  REGISTER
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Toolbar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "250px",
            backgroundColor: "rgb(40, 44, 52)",
            color: "white",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton
            onClick={() => setMobileMenuOpen(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <List>
          <ListItem
            button
            component={Link}
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            sx={{
              color: "white",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            <ListItemText primary="Home" />
          </ListItem>

          <ListItem
            button
            component={Link}
            to="/events"
            onClick={() => setMobileMenuOpen(false)}
            sx={{
              color: "white",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            <ListItemText primary="Events" />
          </ListItem>

          <ListItem
            button
            component={Link}
            to="/clips"
            onClick={() => setMobileMenuOpen(false)}
            sx={{
              color: "white",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            <ListItemText primary="Clips" />
          </ListItem>

          {/* Leaderboards Submenu */}
          <ListItem
            button
            onClick={() => toggleMobileSubmenu("leaderboards")}
            sx={{
              color: "white",
              "&:visited": { color: "white" },
              "&:hover": { color: "white" },
            }}
          >
            <ListItemText primary="Leaderboards" />
            <Icon
              icon={
                mobileSubmenuOpen.leaderboards
                  ? "mdi:chevron-up"
                  : "mdi:chevron-down"
              }
            />
          </ListItem>

          {mobileSubmenuOpen.leaderboards && (
            <Box sx={{ pl: 2 }}>
              <ListItem
                button
                component={Link}
                to="/leaderboard"
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  color: "white",
                  "&:visited": { color: "white" },
                  "&:hover": { color: "white" },
                }}
              >
                <ListItemText primary="Clip Leaderboard" />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/blackops6-leaderboard"
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  color: "white",
                  "&:visited": { color: "white" },
                  "&:hover": { color: "white" },
                }}
              >
                <ListItemText primary="Black Ops 6 Leaderboard" />
              </ListItem>
            </Box>
          )}

          {/* Forms Submenu */}
          <ListItem
            button
            onClick={() => toggleMobileSubmenu("forms")}
            sx={{ color: "white" }}
          >
            <ListItemText primary="Forms" />
            <Icon
              icon={
                mobileSubmenuOpen.forms ? "mdi:chevron-up" : "mdi:chevron-down"
              }
            />
          </ListItem>

          {mobileSubmenuOpen.forms && (
            <Box sx={{ pl: 2 }}>
              <ListItem
                button
                component={Link}
                to="/staff-application"
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  color: "white",
                  "&:visited": { color: "white" },
                  "&:hover": { color: "white" },
                }}
              >
                <ListItemText primary="Staff Application" />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/game-interest"
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  color: "white",
                  "&:visited": { color: "white" },
                  "&:hover": { color: "white" },
                }}
              >
                <ListItemText primary="Game Interest Form" />
              </ListItem>
            </Box>
          )}

          {/* Company Submenu */}
          <ListItem
            button
            onClick={() => toggleMobileSubmenu("company")}
            sx={{ color: "white" }}
          >
            <ListItemText primary="Company" />
            <Icon
              icon={
                mobileSubmenuOpen.company
                  ? "mdi:chevron-up"
                  : "mdi:chevron-down"
              }
            />
          </ListItem>

          {mobileSubmenuOpen.company && (
            <Box sx={{ pl: 2 }}>
              <ListItem
                button
                component={Link}
                to="/ranks"
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  color: "white",
                  "&:visited": { color: "white" },
                  "&:hover": { color: "white" },
                }}
              >
                <ListItemText primary="Ranks" />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/code-of-conduct"
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  color: "white",
                  "&:visited": { color: "white" },
                  "&:hover": { color: "white" },
                }}
              >
                <ListItemText primary="Code of Conduct" />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/faq"
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  color: "white",
                  "&:visited": { color: "white" },
                  "&:hover": { color: "white" },
                }}
              >
                <ListItemText primary="FAQ" />
              </ListItem>
            </Box>
          )}

          {isAdmin && (
            <ListItem
              button
              component={Link}
              to="/admin-dashboard"
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: "white" }}
            >
              <ListItemText primary="Admin Dashboard" />
            </ListItem>
          )}
        </List>

        {/* User info at bottom */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {isLoggedIn ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  src={
                    user?.avatar
                      ? `${pb.baseUrl}/api/files/users/${user.id}/${user.avatar}`
                      : null
                  }
                  sx={{ width: 32, height: 32, mr: 1 }}
                  imgProps={{
                    crossOrigin: "anonymous",
                    referrerPolicy: "no-referrer",
                  }}
                />
                <Box sx={{ color: "white" }}>{user?.username}</Box>
              </Box>
              <Button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                sx={{ color: "white" }}
              >
                LOGOUT
              </Button>
            </>
          ) : (
            <>
              <Button
                component={Link}
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                sx={{ color: "white" }}
              >
                LOGIN
              </Button>
              <Button
                component={Link}
                to="/register"
                onClick={() => setMobileMenuOpen(false)}
                sx={{ color: "white" }}
              >
                REGISTER
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navigation;
